/**
 * Login Component
 *
 * This component handles the user authentication process, including login and password reset functionalities.
 * It validates user input, performs an API request to log the user in, and redirects the user to the dashboard or change password page.
 *
 * ### Features:
 * 1. **Login Functionality**:
 *    - Validates email and password inputs using utility functions.
 *    - Makes an API request to authenticate the user.
 *    - Updates the authentication context (`AuthContext`) with user data and token on successful login.
 *    - Redirects the user to the dashboard or change password page based on the API response.
 *
 * 2. **Forgot Password Functionality**:
 *    - Allows users to request a password reset by providing their email.
 *    - Validates the reset email and sends a password reset request via the API.
 *    - Displays a success message or error based on the API response.
 *
 * 3. **Error Handling**:
 *    - Displays validation errors for email and password fields.
 *    - Handles API errors and displays appropriate error messages to the user.
 *
 * 4. **Loading State**:
 *    - Manages a loading state during API requests to provide feedback to the user.
 *
 * 5. **UI State Management**:
 *    - Toggles between login and forgot password views.
 *    - Manages error messages and loading states dynamically.
 *
 * 6. **OTP Login Support**:
 *    - Enables login via phone number using one-time password (OTP).
 *    - Sends OTP when phone number is submitted.
 *    - Verifies the entered OTP and logs in the user on success.
 *    - Displays a popup confirmation when the OTP is sent.
 *
 * ### Dependencies:
 * - **React**: For building the component and managing state.
 * - **React Router**: For navigation and redirection (`useNavigate`).
 * - **LoginLogic Component**: For rendering the login UI and handling user interactions.
 * - **Validation Utilities**: `validateEmail` and `validatePassword` for input validation.
 * - **Dependency Injection Container**: Resolves services like `ApiService` and `Logger`.
 * - **ApiService**: Handles API requests for login and password reset.
 * - **Logger**: Logs errors and other important events.
 * - **AuthContext**: Manages global authentication state and provides the `login` function.
 *
 * ### Workflow:
 * 1. The user enters their email and password.
 * 2. The component validates the inputs and displays errors if any.
 * 3. On successful validation, an API request is made to log the user in.
 * 4. If the login is successful:
 *    - The user data and token are stored in the `AuthContext`.
 *    - The user is redirected to the dashboard or change password page (if required).
 * 5. If the login fails, an error message is displayed.
 * 6. The user can toggle to the "Forgot Password" view to request a password reset.
 * 7. On successful password reset request, a confirmation message is displayed.
 *
 * ### Error Handling:
 * - Validation errors (e.g., invalid email or password) are displayed immediately.
 * - API errors (e.g., invalid credentials, server issues) are logged and displayed to the user.
 *
 * ### Usage:
 * This component is typically used as the entry point for user authentication in the application.
 * It should be placed in a route (e.g., `/login`) and integrated with the application's routing system.
 */
import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import LoginLogic from "../components/LoginLogic"
import {
  validateEmail,
  validatePassword,
} from "../../infrastructure/utils/validation"
import { container } from "../../infrastructure/container"
import { ApiService } from "../../infrastructure/services/api"
import { Logger } from "../../infrastructure/services/logger"
import { useAuth } from "../context/AuthContext"
import { ServerError } from "../../infrastructure/models/enums/servererror"
import { LocalError } from "../../infrastructure/models/enums/localerror"
import { TYPES } from "../models/types"

// Interfaces for managing form state
interface FormState {
  phone: string
  email: string
  password: string
  resetEmail: string
}

interface UiState {
  error: {
    code: string
    message: string
    details?: {
      email?: string
      password?: string
    }
  } | null
  loading: boolean
  forgotPassword: boolean
}

const Login: React.FC = () => {
  // Resolve services using dependency injection
  const apiService = container.resolve<ApiService>("apiService")
  const logger = container.resolve<Logger>("logger")

  // Use React Router's `useNavigate` hook to handle redirection
  const navigate = useNavigate()
  const { login } = useAuth() // `login` is the function from AuthContext to set authentication state

  // Define form and UI states
  const [formState, setFormState] = useState<FormState>({
    phone: "",
    email: "",
    password: "",
    resetEmail: "",
  })

  const [uiState, setUiState] = useState<UiState>({
    error: null,
    loading: false,
    forgotPassword: false,
  })

  const [popupMessage, setPopupMessage] = useState<string | null>(null)
  const [otpSent, setOtpSent] = useState(false)
  const [otp, setOtp] = useState("")
  const [activeTab, setActiveTab] = useState(TYPES.EMAIL)

  const { phone, email, password, resetEmail } = formState
  const { error, loading, forgotPassword } = uiState

  /**
   * Updates specific form field with new value.
   * @param field - The name of the form field.
   * @param value - New value for the field.
   */

  const handleInputChange = (field: keyof FormState, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }))
  }

  /**
   * Updates the UI state fields like `loading`, `error`, or `forgotPassword`.
   * @param field - Field name to update.
   * @param value - New value for the field.
   */
  const updateUiState = (
    field: keyof UiState,
    value:
      | string
      | boolean
      | {
          code: string
          message: string
          details?: {
            email?: string
            password?: string
          }
        }
      | null
  ) => {
    if (field === TYPES.ERROR) {
      setUiState((prev) => ({
        ...prev,
        [field]: value as { code: string; message: string } | null,
      }))
    } else {
      setUiState((prev) => ({ ...prev, [field]: value as string | boolean }))
    }
  }

  /**
   * Enables loading state and clears previous error state.
   */
  const showLoading = () => {
    updateUiState("loading", true)
    updateUiState("error", null)
  }

  /**
   * Clears popup messages after 3 seconds.
   */
  useEffect(() => {
    if (popupMessage) {
      const timer = setTimeout(() => setPopupMessage(null), 1000)
      return () => clearTimeout(timer)
    }
  }, [popupMessage])

  /**
   * Handles login based on the active tab (email/password or phone).
   * Performs validation, invokes login API, and handles navigation.
   */
  const handleLogin = async () => {
    showLoading()
    const isPhoneTab = activeTab === TYPES.PHONE

    try {
      if (isPhoneTab && !otpSent) {
        if (!phone.trim()) {
          updateUiState("error", {
            code: LocalError.VALIDATION_ERROR,
            message: "Phone number is required.",
            details: { email: "Phone number is required" },
          })
          updateUiState("loading", false)
          return
        }

        const response = await apiService.login(
          undefined,
          undefined,
          phone.trim()
        )
        if (!response) throw new Error("Invalid response from server.")

        setPopupMessage("OTP sent successfully")
        setOtpSent(true)
        return
      }

      // Validate credentials
      const emailError = validateEmail(email)
      const passwordError = validatePassword(password)

      if (emailError || passwordError) {
        updateUiState("error", {
          code: LocalError.VALIDATION_ERROR,
          message: LocalError.getDescription(LocalError.VALIDATION_ERROR),
          details: { email: emailError, password: passwordError },
        })
        updateUiState("loading", false)
        return
      }

      const response = await apiService.login(email, password)
      if (!response || !response.user) {
        throw new Error("Login failed: User data missing.")
      }

      if (response.code === ServerError.CHANGE_PASSWORD) {
        alert("You must change your password.")
        navigate("/change-password", { replace: true })
        return
      }

      login(response)
      navigate("/dashboard", { replace: true })
    } catch (err: any) {
      logger.error("Login error:", err)
      updateUiState("error", {
        code: err.code || "LOGIN_FAILED",
        message: err.message || "Login failed.",
      })
    } finally {
      updateUiState("loading", false)
    }
  }

  /**
   * Verifies the OTP entered by the user and logs them in.
   */
  const handleVerifyOtp = async () => {
    showLoading()
    try {
      const response = await apiService.verifyOtp(phone.trim(), otp.trim())

      if (!response || !response.user) {
        throw new Error("OTP verification failed: User data missing.")
      }

      login(response)
      navigate("/dashboard", { replace: true })
    } catch (err: any) {
      logger.error("OTP verification error:", err)
      updateUiState("error", {
        code: err.code || "OTP_VERIFICATION_FAILED",
        message: err.message || "OTP verification failed.",
      })
    } finally {
      updateUiState("loading", false)
    }
  }

  /**
   * Handles the forgot password flow by sending reset link.
   */
  const handleForgotPassword = async () => {
    showLoading()
    const emailError = validateEmail(resetEmail)

    if (emailError) {
      updateUiState("error", { code: error?.code || "", message: emailError })
      updateUiState("loading", false)
      return
    }

    try {
      await apiService.forgotPassword(resetEmail)
      alert("Password reset email sent.")
      updateUiState("forgotPassword", false)
    } catch (err: any) {
      updateUiState("error", {
        code: err.code,
        message: err.message || "Failed to send reset email.",
      })
    } finally {
      updateUiState("loading", false)
    }
  }

  return (
    <LoginLogic
      email={email}
      setemail={(value: string) => handleInputChange("email", value)}
      phone={phone}
      setPhone={(value: string) => handleInputChange("phone", value)}
      password={password}
      setPassword={(value: string) => handleInputChange("password", value)}
      error={error}
      loading={loading}
      handleLogin={handleLogin}
      handleVerifyOtp={handleVerifyOtp}
      forgotPassword={forgotPassword}
      setForgotPassword={(value: boolean) =>
        updateUiState("forgotPassword", value)
      }
      resetEmail={resetEmail}
      setResetEmail={(value: string) => handleInputChange("resetEmail", value)}
      handleForgotPassword={handleForgotPassword}
      popupMessage={popupMessage}
      otpSent={otpSent}
      setOtpSent={setOtpSent}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      otp={otp}
      setOtp={setOtp}
    />
  )
}

export default Login